import { Multistep, useFormApi, useMultistepApi } from 'informed';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StepProps } from './type';
import { Button } from '@/components/buttons';
import { InputDate } from '@/components/form/Date';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Input } from '@/components/form/Input';
import { InputPhoneNumber } from '@/components/form/InputPhoneNumber';
import { NamesSteps } from '@/helpers/auth';
import { notEmpty } from '@/helpers/common';
import { validateDate, validateName } from '@/helpers/validators';
import { sendAdditionalUserDataStep2NextEvent } from '@/modules/googleAnalytics/events/additionalDataEvents';
import { useCommonStore } from '@/store';

import { ScRegistrationWrapButtons } from '../../../styled';
import { ScFormRegistration } from '@/components/form/groups/styled';

export const PersonalInformationStep: FC<StepProps> = ({ step, errors }) => {
  const { setCurrent, next } = useMultistepApi();
  const { getInitialValue } = useFormApi();

  const { t } = useTranslation(['common', 'validation']);

  const user = useCommonStore.use.user();
  const countryCode = useCommonStore.use.countryCode();

  const defaultCountryCode = useMemo(() => {
    if (
      getInitialValue(`${NamesSteps.PERSONAL_INFORMATION}.mobilePhone.code`) !=
        null ||
      countryCode == null
    )
      return;

    return countryCode;
  }, [getInitialValue, countryCode]);

  const [mobilePhoneError, setMobilePhoneError] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (
      errors.find(
        ({ field }) =>
          field === `${NamesSteps.PERSONAL_INFORMATION}.mobilePhone`
      )
    ) {
      setMobilePhoneError(t('validation:mobilePhone.invalid'));
      setCurrent(NamesSteps.PERSONAL_INFORMATION);
    }
  }, [errors, setCurrent, t]);

  return (
    <Multistep.Step step={step}>
      <ScFormRegistration>
        <FormGroup title={t('auth.registration.form.firstName')}>
          <Input
            placeholder={t('auth.registration.form.firstName')}
            validateOn="blur"
            name="firstName"
            validate={validateName}
          />
        </FormGroup>
        <FormGroup title={t('auth.registration.form.lastName')}>
          <Input
            placeholder={t('auth.registration.form.lastName')}
            validateOn="blur"
            name="lastName"
            validate={validateName}
          />
        </FormGroup>
        <FormGroup title={t('auth.registration.form.dateOfBirth')}>
          <InputDate
            validate={validateDate(countryCode)}
            disabled={notEmpty(user?.dateOfBirth)}
            name="dateOfBirth"
          />
        </FormGroup>
        <FormGroup title={t('auth.registration.form.phoneNumber')}>
          <InputPhoneNumber
            name="mobilePhone"
            initialValue={getInitialValue(
              `${NamesSteps.PERSONAL_INFORMATION}.mobilePhone.number`
            )}
            initialCodeValue={defaultCountryCode}
            withValidate
            validateOnBlur
            disabled={notEmpty(user?.mobilePhone)}
            error={mobilePhoneError}
            onBlur={() => setMobilePhoneError(undefined)}
          />
        </FormGroup>
      </ScFormRegistration>
      <ScRegistrationWrapButtons cols={1}>
        <Button
          primary
          wide
          onClick={() => {
            sendAdditionalUserDataStep2NextEvent();
            next();
          }}
          large
        >
          {t('auth.registration.buttons.next')}
        </Button>
      </ScRegistrationWrapButtons>
    </Multistep.Step>
  );
};
